import { useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, X, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Social Share Modal — extracted from DesignStudio.jsx (Feb 2026 refactor)
export default function SocialShareModal({ isOpen, onClose, design, conceptImage, language }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const designName = design?.design_specs?.name || 'AI Design';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://nexusdesignai.com';

  const shareText = language === 'tr'
    ? `🚀 Nexus Design AI ile "${designName}" tasarladım! AI destekli prototip tasarım platformu ile fikirlerinizi gerçeğe dönüştürün.`
    : `🚀 I designed "${designName}" with Nexus Design AI! Transform your ideas into reality with AI-powered prototype design.`;

  const hashtags = 'NexusDesignAI,AI,Design,Prototype,Innovation';

  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: designName, text: shareText, url: shareUrl });
      } catch {
        // cancelled
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-emerald-500/30 rounded-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
          <div className="flex items-center gap-3">
            <Share2 className="w-6 h-6 text-emerald-400" />
            <div>
              <h3 className="text-lg font-bold text-white">
                {language === 'tr' ? 'Tasarımı Paylaş' : 'Share Design'}
              </h3>
              <p className="text-sm text-slate-400">{designName}</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4">
          <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
            {conceptImage && (
              <img src={conceptImage} alt={designName} className="w-full h-32 object-cover rounded-lg mb-3" />
            )}
            <p className="text-slate-300 text-sm">{shareText}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button onClick={shareToTwitter} className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white flex items-center justify-center gap-2">
              <Twitter className="w-5 h-5" /> Twitter
            </Button>
            <Button onClick={shareToLinkedIn} className="bg-[#0A66C2] hover:bg-[#094d92] text-white flex items-center justify-center gap-2">
              <Linkedin className="w-5 h-5" /> LinkedIn
            </Button>
            <Button onClick={copyToClipboard} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 flex items-center justify-center gap-2">
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              {copied ? (language === 'tr' ? 'Kopyalandı!' : 'Copied!') : (language === 'tr' ? 'Kopyala' : 'Copy')}
            </Button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <Button onClick={nativeShare} variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20 flex items-center justify-center gap-2">
                <Share2 className="w-5 h-5" />
                {language === 'tr' ? 'Diğer' : 'More'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
